import { ReactNode, useEffect, useRef, useState } from "react";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import {
  ReturnActionKind,
  ReturnActionRequest,
  ReturnCondition,
  ReturnDetail,
} from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY, INBOX_DAY_TIME } from "../../utils/inboxDates";
import PriceFormat from "../../utils/priceFormat";
import {
  actionLabel,
  actionTone,
  ActionTone,
  conditionLabel,
  deadlineText,
} from "../../utils/returnsRules";
import { trackingLink } from "../../utils/trackingLink";
import { IInboxNotice, InboxNotice } from "../messagesInbox/InboxBits";
import AcceptPanel from "./AcceptPanel";
import { ReturnActionPanelProps } from "./ActionPanelFrame";
import DeclinePanel from "./DeclinePanel";
import EscalatePanel from "./EscalatePanel";
import FullRefundKeepPanel from "./FullRefundKeepPanel";
import MarkReceivedPanel from "./MarkReceivedPanel";
import MessagePanel from "./MessagePanel";
import PartialOfferPanel from "./PartialOfferPanel";
import ReadOnlyActions from "./ReadOnlyActions";
import RefundPanel from "./RefundPanel";
import RmaPanel from "./RmaPanel";
import UploadLabelPanel from "./UploadLabelPanel";

const buttonTone: Record<ActionTone, string> = {
  primary: "bg-primary-600 text-white hover:bg-primary-700",
  default: "border border-gray-300 bg-white text-gray-900 hover:bg-gray-50",
  danger:
    "border border-red-200 bg-white font-semibold text-red-700 hover:bg-red-50",
};

/** Values a panel opens with when another part of the page opens it (Refund now, Add return tracking). */
export interface ReturnPanelPrefill {
  condition?: ReturnCondition;
  deductionPct?: number;
  /** A template to start the message from, e.g. "own_tracking". */
  templateKey?: string;
}

/** Ask the block to open a panel. A new `nonce` opens it again even for the same kind. */
export interface ReturnPanelRequest {
  kind: ReturnActionKind;
  prefill?: ReturnPanelPrefill;
  nonce: number;
}

interface WhatEbayAllowsProps {
  detail: ReturnDetail;
  now: DateTime;
  onAct: (kind: ReturnActionKind, body: ReturnActionRequest) => void;
  acting: boolean;
  /** The last action's outcome from the API, blocked or not. */
  notice: IInboxNotice | null;
  /** The action to open straight away (e.g. the bell's "respond"). */
  initialKind?: ReturnActionKind;
  /** Opens a panel from elsewhere on the page, only if eBay allows that action now. */
  request?: ReturnPanelRequest | null;
  /** Shown under the tracking box: the "Add return tracking" form. */
  trackingSlot?: ReactNode;
}

/** eBay's tracking status when it gives no date: its codes in words, never the raw value. */
function deliveryText(status: string | null | undefined, t: (key: string) => string) {
  switch ((status ?? "").trim().toUpperCase()) {
    case "":
      return t("Not known");
    case "IN_TRANSIT":
      return t("No date yet · in transit");
    case "DELIVERED":
      return t("Delivered");
    case "OUT_FOR_DELIVERY":
      return t("Out for delivery");
    default: {
      const words = (status ?? "").toLowerCase().replace(/_/g, " ").trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    }
  }
}

function Panel({
  kind,
  prefill,
  ...props
}: ReturnActionPanelProps & {
  kind: ReturnActionKind;
  prefill?: ReturnPanelPrefill;
}) {
  switch (kind) {
    case "accept":
      return <AcceptPanel {...props} />;
    case "full_refund_keep":
      return <FullRefundKeepPanel {...props} />;
    case "partial_offer":
      return <PartialOfferPanel {...props} />;
    case "decline":
      return <DeclinePanel {...props} />;
    case "provide_rma":
      return <RmaPanel {...props} />;
    case "upload_label":
      return <UploadLabelPanel {...props} />;
    case "mark_received":
      return <MarkReceivedPanel {...props} />;
    case "refund_full":
    case "refund_deduction":
      return (
        <RefundPanel
          kind={kind}
          initialCondition={prefill?.condition}
          initialPct={prefill?.deductionPct}
          {...props}
        />
      );
    case "message":
      return (
        <MessagePanel initialTemplateKey={prefill?.templateKey} {...props} />
      );
    case "escalate":
      return <EscalatePanel {...props} />;
    default:
      return null;
  }
}

/** A tracking number, linked to the courier's page when we know how to build one. */
function TrackingNumber({
  tracking,
  carrier,
}: {
  tracking: string | null;
  carrier: string | null;
}) {
  if (!tracking) return <span className="text-gray-400">—</span>;
  const href = trackingLink({
    trackingNumber: tracking,
    courier: carrier ?? undefined,
  });
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-mono font-semibold text-primary-700 hover:underline"
    >
      {tracking} <ArrowTopRightOnSquareIcon className="h-3 w-3" />
    </a>
  ) : (
    <span className="font-mono font-semibold">{tracking}</span>
  );
}

/** Heading over eBay's own tracking once the return is coming back another way. */
function NotUsedCaption() {
  const { t } = useTranslation("returns");
  return (
    <span className="col-span-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {t("eBay label (not used)")}
    </span>
  );
}

/** The courier and tracking added in Wilma: the one to watch. */
function OwnTracking({
  own,
}: {
  own: NonNullable<ReturnDetail["ownTracking"]>;
}) {
  const { t } = useTranslation("returns");
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-2.5 gap-y-1 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2.5 text-[12px]">
      <span className="col-span-2 text-[11px] font-semibold uppercase tracking-wide text-primary-800">
        {t("Return tracking · added in Wilma")}
      </span>
      <span className="text-gray-500">{t("Tracking")}</span>
      <TrackingNumber tracking={own.tracking} carrier={own.carrier} />
      <span className="text-gray-500">{t("Carrier")}</span>
      <span>{own.carrier}</span>
      {own.at && (
        <>
          <span className="text-gray-500">{t("Added")}</span>
          <span>{formatDateTime(own.at, INBOX_DAY_TIME)}</span>
        </>
      )}
    </div>
  );
}

/** The return label eBay (or we) issued, shown as soon as it exists — eBay shows the tracking before the buyer posts. */
function LabelTracking({
  label,
  superseded,
}: {
  label: NonNullable<ReturnDetail["label"]>;
  superseded: boolean;
}) {
  const { t } = useTranslation("returns");
  return (
    <div
      className={classNames(
        "grid grid-cols-[110px_minmax(0,1fr)] gap-x-2.5 gap-y-1 rounded-lg border border-gray-200 px-3 py-2.5 text-[12px]",
        superseded && "text-gray-500",
      )}
    >
      {superseded && <NotUsedCaption />}
      <span className="text-gray-500">{t("Tracking")}</span>
      <TrackingNumber tracking={label.tracking} carrier={label.carrier} />
      <span className="text-gray-500">{t("Carrier")}</span>
      <span>
        {[
          label.carrier,
          label.source === "ebay"
            ? t("eBay label")
            : label.source === "own"
              ? t("our label")
              : null,
        ]
          .filter(Boolean)
          .join(" · ") || "—"}
      </span>
      <span className="text-gray-500">{t("Posted")}</span>
      <span className="text-gray-500">{t("Not yet, label issued")}</span>
    </div>
  );
}

/** The state-specific box above the buttons: where the buyer's parcel is, the offer on the table, and so on. */
function StateContext({
  detail,
  now,
}: {
  detail: ReturnDetail;
  now: DateTime;
}) {
  const { t } = useTranslation("returns");
  const state = detail.ebayState;
  const deadline = deadlineText(detail.deadline, now);

  if (
    state === "ITEM_READY_TO_SHIP" &&
    detail.deadline.kind === "post" &&
    detail.deadline.at
  ) {
    return (
      <>
        <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 text-[12px]">
          <span className="text-gray-700">
            {t("Buyer to post by")}{" "}
            <strong className="font-semibold">
              {formatDateTime(detail.deadline.at, INBOX_DAY)}
            </strong>{" "}
            · {t("eBay's send-by date")}
          </span>
          {deadline && (
            <span
              className={classNames(
                "rounded px-[7px] py-0.5 text-[11px] font-semibold",
                deadline.tone === "red"
                  ? "bg-red-100 text-red-700"
                  : deadline.tone === "amber"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-gray-100 text-gray-700",
              )}
            >
              {deadline.text}
            </span>
          )}
        </div>
        {detail.label?.tracking && (
          <LabelTracking label={detail.label} superseded={!!detail.ownTracking} />
        )}
      </>
    );
  }

  if (state === "ITEM_SHIPPED" && detail.buyerShipment) {
    const s = detail.buyerShipment;
    const superseded = !!detail.ownTracking;
    return (
      <div
        className={classNames(
          "grid grid-cols-[110px_minmax(0,1fr)] gap-x-2.5 gap-y-1 rounded-lg border border-gray-200 px-3 py-2.5 text-[12px]",
          superseded && "text-gray-500",
        )}
      >
        {superseded && <NotUsedCaption />}
        <span className="text-gray-500">{t("Tracking")}</span>
        <TrackingNumber tracking={s.tracking} carrier={s.carrier} />
        <span className="text-gray-500">{t("Carrier")}</span>
        <span>
          {[
            s.carrier,
            detail.label?.source === "ebay"
              ? t("eBay label")
              : detail.label?.source === "own"
                ? t("our label")
                : null,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </span>
        <span className="text-gray-500">{t("Posted")}</span>
        <span>
          {s.shippedAt ? formatDateTime(s.shippedAt, INBOX_DAY_TIME) : "—"}{" "}
          <span className="text-gray-500">· {t("from eBay")}</span>
        </span>
        <span className="text-gray-500">{t("Expected")}</span>
        <span className="font-semibold">
          {s.expectedAt
            ? formatDateTime(s.expectedAt, INBOX_DAY)
            : deliveryText(s.deliveryStatus, t)}
        </span>
      </div>
    );
  }

  if (detail.deadline.kind === "refund" && detail.deadline.at) {
    const i = detail.inspection;
    return (
      <>
        <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px]">
          <span className="text-amber-900">
            {t("Refund by")}{" "}
            <strong className="font-semibold">
              {formatDateTime(detail.deadline.at, INBOX_DAY_TIME)}
            </strong>{" "}
            · {t("2 business days (eBay), or eBay refunds automatically")}
          </span>
          {deadline && (
            <span
              className={classNames(
                "text-[14px] font-bold",
                deadline.tone === "red" ? "text-red-700" : "text-amber-700",
              )}
            >
              {deadline.text}
            </span>
          )}
        </div>
        {i && (
          <div className="flex flex-col gap-1 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-[12px]">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-green-800">
              {t("Received and checked")}
              {i.by ? ` · ${i.by}` : ""} ·{" "}
              {formatDateTime(i.at, INBOX_DAY_TIME)}
            </span>
            <span>
              {t("Correct part")}{" "}
              {i.correctPart ? (
                "✓"
              ) : (
                <strong className="font-semibold text-red-700">
                  {t("No")}
                </strong>
              )}{" "}
              · {t("Part no. matches")}{" "}
              {i.partNumberMatches ? (
                "✓"
              ) : (
                <strong className="font-semibold text-red-700">
                  {t("No")}
                </strong>
              )}{" "}
              · {t("Same condition as dispatched")}{" "}
              {i.sameCondition ? (
                "✓"
              ) : (
                <strong className="font-semibold text-red-700">
                  {t("No")}
                </strong>
              )}
              {i.difference ? ` — "${i.difference}"` : ""}.{" "}
              <strong className="font-semibold">
                {t("Condition: {{condition}}.", {
                  condition: conditionLabel(i.condition),
                })}
              </strong>{" "}
              {i.photos?.length
                ? t("{{count}} photos.", { count: i.photos.length })
                : ""}
              {i.locatedBin || i.location
                ? ` ${t("Located {{where}}", { where: i.locatedBin ?? i.location })}${i.relistedAt ? `, ${t("relisted")}` : ""}.`
                : ""}
            </span>
          </div>
        )}
      </>
    );
  }

  if (state === "PARTIAL_REFUND_REQUESTED" && detail.partialOffer) {
    const o = detail.partialOffer;
    return (
      <div className="flex flex-col gap-1 rounded-lg border border-gray-200 px-3 py-2.5 text-[12px]">
        <span>
          {t("You offered")}{" "}
          <strong className="font-semibold">
            {t("{{amount}} of {{of}}", {
              amount: PriceFormat(o.amount),
              of: PriceFormat(o.of),
            })}
          </strong>
          , {t("buyer keeps the item")}
          {o.at ? ` · ${formatDateTime(o.at, INBOX_DAY_TIME)}` : ""}
          {o.by ? ` · ${o.by}` : ""}
        </span>
      </div>
    );
  }

  if (state === "RETURN_REJECTED") {
    return (
      <div className="flex flex-col gap-1 rounded-lg border border-gray-200 px-3 py-2.5 text-[12px]">
        <span>
          {t("Declined")}
          {detail.stageBy ? ` ${t("by {{who}}", { who: detail.stageBy })}` : ""}
          {detail.stageAt
            ? ` · ${formatDateTime(detail.stageAt, INBOX_DAY_TIME)}`
            : ""}
        </span>
        <span className="text-gray-500">
          {t("The buyer can ask eBay to step in for 3 business days.")}
        </span>
      </div>
    );
  }

  return null;
}

/** A line under the buttons about what happens on its own in this state. */
function StateFootnote({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  switch (detail.ebayState) {
    case "ITEM_READY_TO_SHIP":
      return (
        <span className="text-[11px] text-gray-500">
          {t(
            "If nothing is posted by the send-by date eBay may close the return.",
          )}
        </span>
      );
    case "ITEM_SHIPPED":
      return (
        <span className="text-[11px] text-gray-500">
          {t(
            "When it arrives, fill in Received and checked on this page — that tells eBay it's back and starts eBay's 2-business-day refund clock.",
          )}
        </span>
      );
    case "PARTIAL_REFUND_REQUESTED":
      return (
        <span className="text-[11px] text-gray-500">
          {t(
            "The buyer can accept the offer (eBay refunds and closes) or decline it, after which the original options return. Either side can ask eBay to step in after 3 business days without agreement.",
          )}
        </span>
      );
    default:
      return null;
  }
}

/**
 * The option buttons eBay offers for this reason, policy and state. Only what the API lists in
 * `allowedActions` is shown — the dashboard never works the buttons out itself.
 */
export default function WhatEbayAllows({
  detail,
  now,
  onAct,
  acting,
  notice,
  initialKind,
  request,
  trackingSlot,
}: WhatEbayAllowsProps) {
  const { t } = useTranslation("returns");
  const [open, setOpen] = useState<ReturnActionKind | null>(
    initialKind && detail.allowedActions.includes(initialKind)
      ? initialKind
      : null,
  );
  const [prefill, setPrefill] = useState<ReturnPanelPrefill | undefined>();
  const [panelKey, setPanelKey] = useState(0);
  const rootRef = useRef<HTMLElement>(null);
  const isEbay = detail.channel === "ebay";

  // Another part of the page asked for a panel: open it (fresh, with its prefill) and bring it into view.
  useEffect(() => {
    if (!request || !detail.allowedActions.includes(request.kind)) return;
    setOpen(request.kind);
    setPrefill(request.prefill);
    setPanelKey(request.nonce);
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new request (nonce) opens a panel
  }, [request?.nonce]);

  // Nothing allowed doesn't mean closed: an open return can be waiting on the buyer or eBay.
  const closed = detail.stage === "Closed" || detail.ebayState === "CLOSED";
  const readOnly =
    detail.escalated || closed || detail.allowedActions.length === 0;
  // eBay sometimes gives only a refund total; a deduction needs the item part on its own.
  const deductionUnavailable =
    detail.estimatedRefund?.item === null ||
    detail.estimatedRefund?.item === undefined;

  // A sent (or blocked) action closes its panel; an error keeps it open so nothing typed is lost.
  useEffect(() => {
    if (notice && notice.tone !== "red") setOpen(null);
  }, [notice]);

  return (
    <article
      ref={rootRef}
      className="scroll-mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white"
    >
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-[13px] font-semibold text-gray-900">
          {isEbay ? t("What eBay allows now") : t("What you can do now")}
        </h2>
        <span className="text-[11px] text-gray-500">
          {isEbay
            ? t("Options eBay offers for this reason, policy and state")
            : t("Wilma's own stages — no eBay involved")}
        </span>
      </div>
      <div className="flex flex-col gap-2.5 px-4 pt-3">
        {detail.ownTracking && <OwnTracking own={detail.ownTracking} />}
        <StateContext detail={detail} now={now} />
        {trackingSlot}
      </div>
      {readOnly ? (
        <div className="pt-3">
          <ReadOnlyActions detail={detail} />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            {detail.allowedActions.map((kind) => {
              const tone = actionTone(kind);
              const pressed = open === kind;
              const unavailable =
                kind === "refund_deduction" && deductionUnavailable;
              return (
                <button
                  key={kind}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => {
                    setPrefill(undefined);
                    setOpen(pressed ? null : kind);
                  }}
                  disabled={acting || unavailable}
                  title={
                    unavailable
                      ? t("eBay hasn't itemised this refund")
                      : undefined
                  }
                  className={classNames(
                    "rounded-lg px-3.5 py-2 text-[13px] font-medium disabled:opacity-50",
                    pressed && tone !== "primary"
                      ? "border border-primary-600 bg-primary-50 text-gray-900"
                      : buttonTone[tone],
                  )}
                >
                  {kind === "refund_full" && detail.estimatedRefund
                    ? `${actionLabel(kind)} · ${PriceFormat(detail.estimatedRefund.total)}`
                    : actionLabel(kind)}
                </button>
              );
            })}
            {detail.declineUnavailableReason &&
              !detail.allowedActions.includes("decline") && (
                <span className="ml-auto self-center text-[11px] text-gray-500">
                  {detail.declineUnavailableReason}
                </span>
              )}
            {deductionUnavailable &&
              detail.allowedActions.includes("refund_deduction") && (
                <span className="self-center text-[11px] text-gray-500">
                  {t(
                    "Refund with a deduction isn't available — eBay hasn't itemised this refund.",
                  )}
                </span>
              )}
          </div>
          {open && (
            <Panel
              key={`${open}-${panelKey}`}
              kind={open}
              prefill={prefill}
              detail={detail}
              busy={acting}
              onCancel={() => setOpen(null)}
              onConfirm={(body) => onAct(open, body)}
            />
          )}
          {notice && <InboxNotice notice={notice} className="mx-4 mb-4" />}
          <div className="px-4 pb-3 empty:hidden">
            <StateFootnote detail={detail} />
          </div>
        </>
      )}
    </article>
  );
}
